import type { ReactNode } from "react";
import { useEffect } from "react";
import { useFetcher } from "react-router";

import { API_DEFAULT_CACHE_HEADERS } from "~/commerce-api/commerce.server";

import type { SerializeFrom } from "~/lib/types";

export const loader = async ({ request, context: { api } }: LoaderArgs) => {
  const searchParams = new URL(request.url).searchParams;
  const q = searchParams.get("q");
  if (!q) {
    throw {};
  }
  const suggestions = await api.shopperSearch.getSearchSuggestions({
    parameters: {
      q,
      limit: 6,
    },
    headers: API_DEFAULT_CACHE_HEADERS,
  });

  return {
    suggestions,
  };
};

export const SearchSuggestionsResource = ({
  q,
  children,
}: {
  q: string;
  children: (
    data: SerializeFrom<typeof loader> | undefined,
    state: "idle" | "loading" | "submitting",
  ) => ReactNode;
}) => {
  const { data, state, load } = useFetcher<typeof loader>({
    key: "search-suggestions",
  });

  useEffect(() => {
    if (!q || q.length < 3) return;
    load("/resources/search-suggestions?q=" + encodeURIComponent(q));
  }, [q]);

  useEffect(() => {
    if (!data) return;
    if (!q || q.length < 3) return;

    const blurrableElements = document.querySelectorAll("#blurrable-content");
    const headerContent = document.querySelector("#nav-content");

    if (blurrableElements.length && !q)
      blurrableElements.forEach(
        e => e.classList.remove("blur", "pointer-events-none"),
        headerContent?.classList.remove(
          "hover:bg-transparent",
          "focus-within:bg-transparent",
        ),
      );
    if (blurrableElements.length && data)
      blurrableElements.forEach(
        e => e.classList.add("blur", "pointer-events-none"),
        headerContent?.classList.add(
          "hover:bg-transparent",
          "focus-within:bg-transparent",
        ),
      );
  }, [q, data]);

  return children(data, state);
};
